import { Intent } from '@blueprintjs/core';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import { TrackingEventList } from 'portal-sdk';
import { alphaSorter } from 'vet-bones/utils';
import { sendHubspotEvent } from 'vet-bones/utils/portal';

import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import {
  PublishFileUploadResponse,
  publishFileUploads,
} from 'src/actions/projects/action-creators/publishProject/publishFileUploads';
import { publishFileUploadsMetadata } from 'src/actions/projects/action-creators/publishProject/publishFileUploadsMetadata';
import { publishModel } from 'src/actions/projects/action-creators/publishProject/publishModel';
import { publishQuestions } from 'src/actions/projects/action-creators/publishProject/publishQuestions';
import { publishRules } from 'src/actions/projects/action-creators/publishProject/publishRules';
import { publishSensitivePropertyGroups } from 'src/actions/projects/action-creators/publishProject/publishSensitivePropertyGroups';
import { publishUniqueKeySets } from 'src/actions/projects/action-creators/publishProject/publishUniqueKeySets';
import {
  PublishVirtualGraphResponse,
  publishVirtualGraphs,
} from 'src/actions/projects/action-creators/publishProject/publishVirtualGraphs';
import { updateDatabaseReasoningSchemas } from 'src/actions/projects/action-creators/publishProject/updateDatabaseReasoningSchemas';
import {
  PublishProjectResponse,
  projectsActionCreators,
} from 'src/actions/projects/actionCreators';
import { CompetencyQuestion } from 'src/constants/voicebox';
import {
  CreationType,
  CsvMappingInfoByProjectFileUploadId,
  DatabaseInfo,
  ModelAliasInfo,
  ModelInfo,
  ModelsInfo,
  RulesInfo,
  SensitivePropertyGroupsInfo,
  VirtualGraphMappingInfoByProjectVgId,
} from 'src/containers/PublishProjectDrawer/types';
import { dataModelItemsSelector } from 'src/selectors/dataModelItems';
import { dataSourceTypeDescriptionSelector } from 'src/selectors/dataSourceTypeDescription';
import { namedGraphsSelector } from 'src/selectors/namedGraphs';
import { ProjectVirtualGraph, PublishHistory } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';
import * as copy from 'src/templates/copy';
import { exportResources } from 'src/utils/export/resources';
import { PublishFileUpload, PublishVirtualGraph } from 'src/utils/export/types';
import { getValidMappingsByResource } from 'src/utils/export/validateMappingsByResource';
import { getDanglingNamedGraphs } from 'src/utils/getDanglingNamedGraphs';

export const publishProject =
  ({
    projectId,
    publishedDate,
    databaseInfo,
    doExportProject,
    isConsolidatingModels,
    isFileIriForImportSupported,
    isPublishingRules,
    modelAliasInfo,
    modelsInfo,
    csvMappingInfo,
    vgMappingInfo,
    ruleInfo,
    spgInfo,
    waitForEvaluatedQuestions,
  }: {
    projectId: string;
    publishedDate: number;
    databaseInfo: DatabaseInfo;
    doExportProject: boolean;
    isConsolidatingModels: boolean;
    isFileIriForImportSupported: boolean;
    isPublishingRules: boolean;
    modelAliasInfo?: ModelAliasInfo;
    modelsInfo: ModelsInfo;
    csvMappingInfo?: CsvMappingInfoByProjectFileUploadId;
    vgMappingInfo?: VirtualGraphMappingInfoByProjectVgId;
    ruleInfo: RulesInfo;
    spgInfo: SensitivePropertyGroupsInfo;
    waitForEvaluatedQuestions: () => Promise<CompetencyQuestion[] | null>;
  }) =>
  async (
    dispatch: AppDispatch,
    getState: ReduxStateGetter
  ): Promise<boolean> => {
    const {
      databaseId,
      shouldEnableVoicebox,
      isPublishingVoiceboxQuestions,
      isPublishingSPG,
    } = databaseInfo;
    const state = getState();
    const {
      dataSources,
      databases,
      projects,
      connections: { connection },
    } = state;
    const project = projects.projectsById[projectId];
    let database = databases.databasesById?.[databaseId];
    if (!project) {
      return false;
    }

    const namedGraphs = namedGraphsSelector(databaseId)(state);
    const dataModelItems = dataModelItemsSelector(databaseId)(state);
    const dangling = getDanglingNamedGraphs({
      dataModelItems,
      namedGraphs,
      modelsInfo,
      modelAliasInfo,
    });

    let modelAliasHistory: PublishHistory['modelAliasHistory'] | undefined;
    if (modelAliasInfo) {
      modelAliasHistory = {
        modelName: modelAliasInfo.modelName,
        namedGraphIris: modelAliasInfo.namedGraphIris,
      };
    }

    const publishHistory: PublishHistory = {
      connectionId: connection?.id || '',
      // exclude `shouldEnableVoicebox` from history since we read this from the db
      databaseInfo: omit(databaseInfo, 'shouldEnableVoicebox'),
      csvResourceHistoriesById: Object.fromEntries(
        Object.entries(csvMappingInfo || {}).map(
          ([key, { creationType, namedGraphIri, shouldPublish }]) => [
            key,
            { creationType, namedGraphIri, shouldPublish },
          ]
        )
      ),
      doExportProject,
      isConsolidatingModels,
      isPublishingRules,
      modelAliasHistory,
      modelHistoriesById: Object.entries(modelsInfo).reduce<
        Record<
          string,
          Pick<ModelInfo, 'modelName' | 'namedGraphIri' | 'shouldPublish'>
        >
      >((acc, [modelId, modelInfo]) => {
        const { modelName, namedGraphIri, shouldPublish } = modelInfo;
        acc[modelId] = {
          modelName,
          namedGraphIri,
          shouldPublish,
        };
        return acc;
      }, {}),
      sensitivePropertyGroupsByName: Object.fromEntries(
        Object.entries(spgInfo || {}).map(
          ([key, { actionType, publishName }]) => [
            key,
            { actionType, publishName },
          ]
        )
      ),
      vgResourceHistoriesById: Object.fromEntries(
        Object.entries(vgMappingInfo || {}).map(
          ([key, { virtualGraphName, shouldPublish }]) => [
            key,
            { virtualGraphName, shouldPublish },
          ]
        )
      ),
    };

    dispatch(
      projectsActionCreators.publishProjectStarted({
        id: projectId,
        databaseId,
        publishedDate,
      })
    );

    const dispatchFinish = (
      intent: Intent,
      responses: PublishProjectResponse[]
    ) => {
      dispatch(
        projectsActionCreators.publishProjectFinished({
          id: projectId,
          name: project.name,
          intent,
          databaseId,
          publishedDate,
          responses,
          publishHistory,
        })
      );
    };

    // create new database (if applicable)
    if (!database) {
      const namedGraphs = isConsolidatingModels
        ? [modelsInfo[project.targetModelId].namedGraphIri]
        : uniq(
            Object.values(modelsInfo).map(
              (modelInfo) => modelInfo.namedGraphIri
            )
          ).sort(alphaSorter);

      const createDbOptions: Record<string, any> = {
        // for optimal Explorer experience
        search: { enabled: true },

        // for the marketplace
        graph: { aliases: true },

        // for new databases, use the model that we're publishing
        reasoning: { 'schema.graphs': namedGraphs.join(',') },
      };
      if (shouldEnableVoicebox) {
        // only add voicebox enabled when true to avoid unsupported versions, etc.
        createDbOptions.voicebox = {
          enabled: true,
        };
      }

      const createDatabaseResponse =
        await databasesStardogRequestDispatchers.createDatabase(
          databaseId,
          createDbOptions
        );

      if (
        createDatabaseResponse.type ===
        DatabasesActionType.CREATE_DATABASE_FAILURE
      ) {
        dispatchFinish(Intent.DANGER, [
          {
            success: false,
            action: createDatabaseResponse,
            actionDescription: copy.notifications.database.createDatabase,
            message: copy.notifications.database.createDatabaseFailure,
          },
        ]);
        return false;
      }

      await Promise.all([
        databasesStardogRequestDispatchers.requestDatabaseDetails(databaseId),
        databasesStardogRequestDispatchers.requestDatabaseNamespaces(
          databaseId
        ),
        databasesStardogRequestDispatchers.requestNamedGraphs(databaseId),
      ]);

      database = getState().databases.databasesById?.[databaseId];
    }

    let competencyQuestions: CompetencyQuestion[] | undefined;
    if (isPublishingVoiceboxQuestions) {
      const updatedQuestions = await waitForEvaluatedQuestions();
      competencyQuestions = (
        updatedQuestions || project.diagram.model.modelData.competencyQuestions
      ).filter(
        (question) =>
          question.evaluation?.query && question.evaluation.score >= 2
      );
    }

    // because Voicebox enabled databases do not allow adding non-empty
    // graphs to the schema via changing db options (`reasoning.schemas`),
    // we must update db options before creating & adding data to the models
    const updateDatabaseReasoningSchemasResponse =
      await updateDatabaseReasoningSchemas({
        database,
        dangling,
        modelAliasInfo,
        modelInfos: isConsolidatingModels
          ? [modelsInfo[project.targetModelId]]
          : Object.values(modelsInfo).filter(
              (modelInfo) => modelInfo.shouldPublish
            ),
        competencyQuestions,
      });

    if (!updateDatabaseReasoningSchemasResponse.success) {
      dispatchFinish(Intent.WARNING, [updateDatabaseReasoningSchemasResponse]);
      return false;
    }

    const publishModelResponses = isConsolidatingModels
      ? [
          await publishModel({
            databaseId,
            danglingNamedGraph:
              dangling[modelsInfo[project.targetModelId].modelName],
            namedGraphs,
            project,
            modelInfo: modelsInfo[project.targetModelId],
            projectModelIds: new Set(
              Object.values(modelsInfo).map(
                (modelInfo) => modelInfo.projectModelId
              )
            ),
            publishedDate,
          }),
        ]
      : await Promise.all(
          Object.values(modelsInfo).map((modelInfo) => {
            return publishModel({
              databaseId,
              danglingNamedGraph: dangling[modelInfo.modelName],
              namedGraphs,
              project,
              modelInfo,
              projectModelIds: new Set([modelInfo.projectModelId]),
              publishedDate,
            });
          })
        );

    // bail early if any models failed to publish
    if (publishModelResponses.some((response) => !response.success)) {
      dispatchFinish(
        publishModelResponses.every((response) => !response.success)
          ? Intent.DANGER
          : Intent.WARNING,
        publishModelResponses
      );
      return false;
    }

    const danglingAliasNamedGraph = dangling[modelAliasInfo?.modelName || ''];
    const publishedNamedGraphs = (
      isConsolidatingModels
        ? [modelsInfo[project.targetModelId]]
        : Object.values(modelsInfo)
    ).map(({ namedGraphIri }) => namedGraphIri);
    if (
      modelAliasInfo?.creationType === CreationType.REPLACE &&
      danglingAliasNamedGraph &&
      // only clear if we didn't just publish a model for this named graph
      !publishedNamedGraphs.includes(danglingAliasNamedGraph)
    ) {
      const clearGraphResponse =
        await databasesStardogRequestDispatchers.clearDatabaseNamedGraph({
          databaseId,
          namedGraph: danglingAliasNamedGraph,
        });

      if (
        clearGraphResponse.type ===
        DatabasesActionType.CLEAR_DATABASE_NAMED_GRAPH_FAILURE
      ) {
        dispatchFinish(Intent.WARNING, [
          {
            success: false,
            action: clearGraphResponse,
            actionDescription: copy.notifications.database.clearNamedGraph,
            message: copy.notifications.publishProject.namedGraph.clearFailure,
          },
        ]);
        return false;
      }
    }

    const successfulModelInfos = isConsolidatingModels
      ? [modelsInfo[project.targetModelId]]
      : [];
    if (!isConsolidatingModels) {
      const modelInfosForIdx = Object.values(modelsInfo);
      Object.values(publishModelResponses).forEach((response, idx) => {
        if (response.success) {
          successfulModelInfos.push(modelInfosForIdx[idx]);
        }
      });
    }

    const publishedModelNames = (
      isConsolidatingModels
        ? [modelsInfo[project.targetModelId]]
        : successfulModelInfos
    ).reduce<Set<string>>((acc, modelInfo, idx) => {
      const response = publishModelResponses[idx];
      if (response?.success) {
        acc.add(modelInfo.modelName);
      }
      return acc;
    }, new Set());

    const publishQuestionsResponses = await publishQuestions({
      competencyQuestions,
      connection,
      database,
      modelsInfo,
      modelAliasInfo,
      project,
    });

    const failedQuestionResponses = publishQuestionsResponses.filter(
      (response) => !response.success
    );

    const publishRulesResponses = await publishRules({
      database,
      project,
      modelsInfo,
      publishedModelNames,
      ruleInfo: isPublishingRules ? ruleInfo : {},
      isConsolidatingModels,
    });
    const failedRuleResponses = publishRulesResponses.filter(
      (response) => !response.success
    );

    const publishSpgsResponse = await publishSensitivePropertyGroups(
      database,
      isPublishingSPG ? spgInfo : {}
    );
    const didSpgsFail = publishSpgsResponse && !publishSpgsResponse.success;

    const { classMappingsByResource, relationshipMappingsByResource } =
      getValidMappingsByResource(project.diagram.model);
    const typeDescription =
      dataSourceTypeDescriptionSelector(connection)(state);

    // Filter out mappings that should not be published
    const filteredCsvMappingInfo = csvMappingInfo
      ? Object.fromEntries(
          Object.entries(csvMappingInfo).filter(
            ([_, info]) => info.shouldPublish
          )
        )
      : undefined;

    const filteredVgMappingInfo = vgMappingInfo
      ? Object.fromEntries(
          Object.entries(vgMappingInfo).filter(
            ([_, info]) => info.shouldPublish
          )
        )
      : undefined;

    const { fileUploadExports, virtualGraphExports } = exportResources({
      project,
      classMappingsByResource,
      relationshipMappingsByResource,
      typeDescription,
      csvMappingInfo: filteredCsvMappingInfo,
      vgMappingInfo: filteredVgMappingInfo,
    });

    const validFileUploads = fileUploadExports.filter(
      (data): data is PublishFileUpload => Boolean(data.mappingInfo)
    );

    const validVirtualGraphs = virtualGraphExports.filter(
      (data): data is PublishVirtualGraph => Boolean(data.mappingInfo)
    );

    // if no valid uploads bail early
    if (!validFileUploads.length && !validVirtualGraphs.length) {
      if (
        failedQuestionResponses.length ||
        failedRuleResponses.length ||
        didSpgsFail
      ) {
        const responses: PublishProjectResponse[] = [];
        if (failedQuestionResponses.length) {
          responses.push(...failedQuestionResponses);
        }
        if (failedRuleResponses.length) {
          responses.push(...failedRuleResponses);
        }
        if (publishSpgsResponse && didSpgsFail) {
          responses.push(publishSpgsResponse);
        }
        dispatchFinish(Intent.WARNING, responses);
        return false;
      }

      dispatchFinish(Intent.SUCCESS, []);

      sendHubspotEvent(TrackingEventList.DESIGNER_PUBLISH_PROJECT);

      return true;
    }

    const fileUploadExportResponses = await publishFileUploads({
      databaseId,
      exports: validFileUploads,
      isFileIriForImportSupported,
    });

    const virtualGraphExportResponses = await publishVirtualGraphs({
      dataSources,
      databaseId,
      exports: validVirtualGraphs,
    });

    const successfulFileUploads: PublishFileUploadResponse[] = [];
    const failedFileUploads: PublishFileUploadResponse[] = [];
    const successfulVirtualGraphs: PublishVirtualGraphResponse[] = [];
    const failedVirtualGraphs: PublishVirtualGraphResponse[] = [];

    fileUploadExportResponses.forEach((response) => {
      if (response.success) {
        successfulFileUploads.push(response);
      } else {
        failedFileUploads.push(response);
      }
    });

    virtualGraphExportResponses.forEach((response) => {
      if (response.success) {
        successfulVirtualGraphs.push(response);
      } else {
        failedVirtualGraphs.push(response);
      }
    });

    const responses: PublishProjectResponse[] = [
      ...publishModelResponses,
      ...publishQuestionsResponses,
      ...publishRulesResponses,
      publishSpgsResponse,
      updateDatabaseReasoningSchemasResponse,
      ...fileUploadExportResponses,
      ...virtualGraphExportResponses,
    ].filter((res): res is PublishProjectResponse => Boolean(res));

    if (!successfulFileUploads.length && !successfulVirtualGraphs.length) {
      dispatchFinish(Intent.WARNING, responses);
      return false;
    }

    // don't need metadata if there are no file uploads at all, or no successful file uploads
    let didFileUploadsMetadataFail = false;
    let publishFileUploadsMetadataResponse: PublishProjectResponse | null =
      null;
    if (successfulFileUploads.length) {
      publishFileUploadsMetadataResponse = await publishFileUploadsMetadata({
        database,
        exports: successfulFileUploads
          .map(({ id }) => validFileUploads.find((upload) => upload.id === id))
          .filter((fileUpload): fileUpload is PublishFileUpload =>
            Boolean(fileUpload)
          ),
        publishedDate,
      });
      didFileUploadsMetadataFail = !publishFileUploadsMetadataResponse.success;

      responses.push(publishFileUploadsMetadataResponse);
    }

    // if there are successful virtual graph uploads, then unique key sets may have to be set for their data sources
    // Unique key sets are now stored on individual mappings, so we publish for all successful VGs
    if (successfulVirtualGraphs.length && vgMappingInfo) {
      const updatedDataSources = new Set<string>(
        successfulVirtualGraphs.map(({ dataSource }) => dataSource)
      );
      const filteredVirtualGraphsById: Record<string, ProjectVirtualGraph> = {};
      Object.values(vgMappingInfo).forEach(({ projectVgId, shouldPublish }) => {
        const virtualGraph = project.virtualGraphsById[projectVgId];
        if (
          shouldPublish &&
          virtualGraph &&
          updatedDataSources.has(virtualGraph.dataSourceId) &&
          // only consider vgs that have mappings with unique key sets
          classMappingsByResource[virtualGraph.id]?.find(
            (mapping) => mapping.uniqueKeySets.length
          )
        ) {
          filteredVirtualGraphsById[virtualGraph.id] = virtualGraph;
        }
      });

      if (Object.keys(filteredVirtualGraphsById).length) {
        dispatch(
          publishUniqueKeySets({
            connection,
            updatedDataSources,
            validClassMappingsByResource: classMappingsByResource,
            virtualGraphsById: filteredVirtualGraphsById,
          })
        );
      }
    }

    if (
      failedQuestionResponses.length ||
      failedRuleResponses.length ||
      didSpgsFail ||
      didFileUploadsMetadataFail ||
      failedFileUploads.length ||
      failedVirtualGraphs.length
    ) {
      dispatchFinish(Intent.WARNING, responses);
      return false;
    }

    dispatchFinish(Intent.SUCCESS, []);

    sendHubspotEvent(TrackingEventList.DESIGNER_PUBLISH_PROJECT);

    return true;
  };
